<template>
  <div class="post-create-container">
    <div class="post-create-wrapper">
      <div class="create-header">
        <h2>📝 发布新帖子</h2>
        <p>分享您的钩织作品与心得</p>
      </div>
      
      <el-form 
        :model="form" 
        :rules="rules" 
        ref="createFormRef"
        label-width="100px"
        class="post-create-form"
      >
        <el-form-item label="帖子标题" prop="title">
          <el-input 
            v-model="form.title" 
            placeholder="请输入帖子标题"
            maxlength="100"
            show-word-limit
            class="custom-input"
          />
        </el-form-item>

        <el-form-item label="帖子内容" prop="content">
          <div class="content-with-ai-btn">
            <el-input
              v-model="form.content"
              type="textarea"
              :rows="6"
              placeholder="请输入帖子内容，分享您的钩织经验、技巧或展示作品..."
              maxlength="10000"
              show-word-limit
              class="custom-textarea"
            />
            <div class="ai-btn" @click="showPolishInstructionDialog = true">AI</div>
          </div>
        </el-form-item>

        <el-form-item label="封面图片">
          <div class="media-uploader-container">
            <div class="media-upload-area" @click="triggerCoverUpload" style="height: 150px;">
              <input
                ref="coverInputRef"
                type="file"
                accept="image/*"
                @change="handleCoverFileChange"
                style="display: none"
              />
              <el-icon v-if="!form.coverImage"><Plus /></el-icon>
            </div>

            <!-- 封面图片预览 -->
            <div v-if="form.coverImage" class="cover-image-container">
              <div class="cover-image-wrapper">
                <img :src="form.coverImage" class="cover-image" />
              </div>
              <div class="media-overlay">
                <span class="media-type">封面</span>
                <i class="delete-btn" @click.stop="removeCoverImage">×</i>
              </div>
            </div>
            
            <p class="image-tip">推荐尺寸：宽高比16:9，大小不超过2MB</p>
          </div>
        </el-form-item>

        <!-- 图片上传区域 -->
        <el-form-item label="内容图片">
          <div class="media-uploader-container">
            <!-- 图片上传按钮 -->
            <div class="media-upload-area" @click="triggerImageUpload">
              <input
                ref="imageInputRef"
                type="file"
                multiple
                accept="image/*"
                @change="handleImageFilesChange"
                style="display: none"
              />
              <el-icon><Plus /></el-icon>
              <span>添加图片</span>
            </div>

            <!-- 图片列表 -->
            <div v-if="imageList.length > 0 || Object.keys(imageUploadProgress).length > 0" class="media-grid-container">
              <draggable 
                v-model="imageList" 
                item-key="id" 
                class="media-grid"
                ghost-class="drag-ghost"
                chosen-class="drag-chosen"
                drag-class="dragging"
                @end="onDragEnd"
              >
                <template #item="{ element, index }">
                  <div class="media-item-wrapper">
                    <div class="media-item">
                      <img :src="processMediaUrl(element.mediaUrl)" class="media-preview" />
                      <div class="media-overlay">
                        <span class="media-type">图片 {{ index + 1 }}</span>
                        <i class="delete-btn" @click="removeMedia(element.id, 'image')">×</i>
                      </div>
                    </div>
                  </div>
                </template>
              </draggable>
              
              <!-- 显示上传进度条 -->
              <div v-for="(progress, key) in imageUploadProgress" :key="'progress-' + key" class="upload-progress-container">
                <div class="upload-progress-item">
                  <div class="progress-label">图片上传中...</div>
                  <el-progress :percentage="progress" :show-text="false" />
                </div>
              </div>
            </div>
            <p class="image-tip">可上传图片，最多9张，拖拽调整顺序</p>
          </div>
        </el-form-item>

        <!-- 视频上传区域 -->
        <el-form-item label="内容视频">
          <div class="media-uploader-container">
            <!-- 视频上传按钮 -->
            <div class="media-upload-area" @click="triggerVideoUpload">
              <input
                ref="videoInputRef"
                type="file"
                accept="video/*"
                @change="handleVideoFileChange"
                style="display: none"
              />
              <el-icon><Plus /></el-icon>
              <span>添加视频</span>
            </div>

            <!-- 视频列表 -->
            <div v-if="videoList.length > 0 || videoUploadProgress > 0" class="media-grid-container">
              <div v-if="videoUploadProgress > 0" class="upload-progress-container">
                <div class="upload-progress-item">
                  <div class="progress-label">视频上传中...</div>
                  <el-progress :percentage="videoUploadProgress" :show-text="false" />
                </div>
              </div>
              
              <div v-for="video in videoList" :key="video.id" class="video-full-container">
                <div class="video-item">
                  <video :src="processMediaUrl(video.mediaUrl)" class="video-full-preview" controls />
                  <div class="video-overlay">
                    <span class="media-type">视频</span>
                    <i class="delete-btn" @click.stop="removeMedia(video.id, 'video')">×</i>
                  </div>
                </div>
              </div>
            </div>
            <p class="image-tip">可上传视频，仅支持一个视频,推荐格式:.mp4</p>
          </div>
        </el-form-item>

        <el-form-item class="form-footer">
          <el-button 
            type="primary" 
            @click="submitForm" 
            :loading="submitting"
            :disabled="!form.title || !form.content || uploadingMedia"
            class="submit-btn"
          >
            {{ uploadingMedia ? '正在上传媒体...' : '发布帖子' }}
          </el-button>
          <el-button @click="resetForm" class="reset-btn">重置</el-button>
          <el-button @click="cancel" class="cancel-btn">取消</el-button>
        </el-form-item>
      </el-form>
    </div>
    
    <!-- AI润色指令输入对话框 -->
    <el-dialog
      v-model="showPolishInstructionDialog"
      title="AI润色指令"
      width="400px"
      center
      class="ai-polish-instruction-dialog"
    >
      <el-input
        v-model="polishInstruction"
        type="textarea"
        :rows="4"
        placeholder="请输入润色指令，例如：让语言更生动有趣、调整为教程风格等"
        maxlength="200"
        show-word-limit
        class="custom-textarea"
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showPolishInstructionDialog = false" class="cancel-btn">取消</el-button>
          <el-button 
            type="primary" 
            @click="startPolishContent" 
            :loading="isPolishing"
            class="confirm-btn"
          >
            {{ isPolishing ? '润色中...' : '开始润色' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- AI润色结果对比对话框 -->
    <el-dialog
      v-model="showPolishResultDialog"
      title="AI润色结果对比"
      width="80%"
      top="5vh"
      class="polish-result-dialog"
    >
      <div class="polish-comparison-container">
        <div class="comparison-panel">
          <h3>原始内容</h3>
          <div class="content-panel original-content">
            <pre>{{ form.content }}</pre>
          </div>
        </div>
        <div class="comparison-panel">
          <h3>AI润色内容</h3>
          <div class="content-panel polished-content">
            <div v-if="isPolishing" class="loading-indicator">
              <el-icon :class="['is-loading']"><Loading /></el-icon>
              <span>AI正在润色中...</span>
            </div>
            <div class="markdown-content" v-html="renderedPolishedContent"></div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer comparison-footer">
          <el-button @click="showPolishResultDialog = false" class="cancel-btn">不接受</el-button>
          <el-button 
            type="primary" 
            @click="acceptPolishedContent" 
            :disabled="isPolishing || !polishedContent"
            class="confirm-btn"
          >
            接受润色结果
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, defineEmits, computed, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Loading } from '@element-plus/icons-vue'
import { useStore } from 'vuex'
import { createPost, uploadCoverImage, uploadImages, uploadVideo, createPostWithMedia, createPostNew, getMediaInfo, type MediaItem, polishContent } from '@/user/api/post'
import draggable from 'vuedraggable'
import MarkdownIt from 'markdown-it'

// 定义emit事件
const emit = defineEmits(['post-created', 'cancel'])

const store = useStore()

// 表单数据
const form = reactive({
  title: '',
  content: '',
  coverImage: '',
  coverImageWidth: 0,
  coverImageHeight: 0
})

// 媒体数据 - 使用shallowReactive以避免深层响应式代理导致的问题
const imageList = ref<any[]>([]);
const videoList = ref<any[]>([]);
const uploadingMedia = ref(false);
const imageInputRef = ref<HTMLInputElement>();
const videoInputRef = ref<HTMLInputElement>();
const coverInputRef = ref<HTMLInputElement>() // 新增封面图片输入引用

// 添加上传进度相关状态
const imageUploadProgress = ref<{ [key: number]: number }>({}); // 图片上传进度
const videoUploadProgress = ref<number>(0); // 视频上传进度

// AI润色相关状态
const showPolishInstructionDialog = ref<boolean>(false);
const showPolishResultDialog = ref<boolean>(false);
const polishInstruction = ref<string>('让语言更生动有趣');
const isPolishing = ref<boolean>(false);
const polishedContent = ref<string>('');
const md = new MarkdownIt();

// 渲染后的Markdown内容
const renderedPolishedContent = computed(() => {
  return polishedContent.value ? md.render(polishedContent.value) : '';
});

// 验证规则
const rules = {
  title: [
    { required: true, message: '请输入帖子标题', trigger: 'blur' },
    { min: 1, max: 100, message: '标题长度应在1-100字符之间', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入帖子内容', trigger: 'blur' },
    { min: 1, max: 10000, message: '内容长度应在1-10000字符之间', trigger: 'blur' }
  ]
}

// 开始AI润色
const startPolishContent = async () => {
  if (!form.content.trim()) {
    ElMessage.warning('请先输入帖子内容');
    return;
  }
  

  showPolishInstructionDialog.value = false;
  showPolishResultDialog.value = true;
  isPolishing.value = true;
  polishedContent.value = '';

  
  try {
    const token = store.state.token || localStorage.getItem('token'); // 获取token

    const apiResponse = await fetch('/api/user/post/polish', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'token': token || '' // 根据项目规范，使用token字段而不是Authorization: Bearer
      },
      body: JSON.stringify({
        content: form.content,           // 需要润色的原始内容
        instruction: polishInstruction.value || '让语言更生动有趣'  // 润色指令
      })
    });


    if (!apiResponse.ok) {
      throw new Error(`润色请求失败: ${apiResponse.status} ${apiResponse.statusText}`);
    }

    const reader = apiResponse.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = ''; // 用于处理可能分割的数据块


    let shouldContinue = true;
    while (shouldContinue) {
      const { done, value } = await reader.read();

      
      if (done) {

        shouldContinue = false;
        break;
      }

      // 解码当前数据块
      const chunk = decoder.decode(value, { stream: true });

      
      buffer += chunk;
      
      // 按行分割缓冲区
      const lines = buffer.split('\n');

      
      // 保留最后一个不完整的行
      buffer = lines.pop() || '';
      
      // 遍历所有行进行处理
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        
        // 检查是否以 'data:' 开头（兼容带空格和不带空格的格式）
        if (line.startsWith('data:')) {

          
          // 提取冒号后的部分，兼容 'data:content' 和 'data: content' 两种格式
          let data = line.substring(5); // 移除 'data:' 部分
          data = data.trimStart(); // 去除开头可能的空格
          

          
          // 检查是否是结束标记（如果有的话）
          if (data === '[DONE]' || data === '') {

            continue;
          }
          
          // 将数据追加到润色内容中

          const previousValue = polishedContent.value;
          
          // 检查当前内容是否以换行符结尾，如果不是且新数据非空，则添加换行符
          if (polishedContent.value.length > 0 && !polishedContent.value.endsWith('\n') && data) {
            polishedContent.value += '\n';
          }
          
          polishedContent.value += data;

          
          // 强制触发Vue响应式更新
          await nextTick();
        } else if (line.trim() === '') {
          // 忽略空行
          console.log('检测到空行，跳过');
        } else {
          console.log('非数据行，跳过:', JSON.stringify(line));
        }
      }
    }
    
    // 处理缓冲区中剩余的数据（防止末尾数据丢失）
    if (buffer) {
      console.log('处理缓冲区中剩余数据:', JSON.stringify(buffer));
      if (buffer.startsWith('data:')) {
        let data = buffer.substring(5); // 移除 'data:' 部分
        data = data.trimStart(); // 去除开头可能的空格
        
        if (data !== '[DONE]' && data !== '') {
          console.log('处理缓冲区最后的数据:', JSON.stringify(data));
          if (polishedContent.value.length > 0 && !polishedContent.value.endsWith('\n') && data) {
            polishedContent.value += '\n';
          }
          polishedContent.value += data;
        }
      }
    }
    

    
    // 如果最终值为空，输出警告
    if (!polishedContent.value) {
      console.warn('polishedContent最终值为空，可能处理过程中出现问题');
    }
  } catch (error: any) {
    console.error('润色内容失败:', error);
    ElMessage.error('润色内容失败: ' + (error.message || '未知错误'));
  } finally {
    isPolishing.value = false;

  }
};

// 接受润色后的内容
const acceptPolishedContent = () => {
  if (polishedContent.value) {
    form.content = polishedContent.value;
    ElMessage.success('已接受润色后的内容！');
    showPolishResultDialog.value = false;
  }
};

// 处理媒体URL，在开发环境中为本地路径添加/api前缀
const processMediaUrl = (url: string | undefined): string => {
  if (!url) {
    return ''; // 如果url是undefined或空值，直接返回空字符串
  }
  
  if (process.env.NODE_ENV === 'development') {
    // 如果是完整URL（以http/https开头）或已经带有/api前缀，则直接返回
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api')) {
      return url;
    }
    // 如果是本地路径（以/开头），添加/api前缀
    else if (url.startsWith('/')) {
      return `/api${url}`;
    }
    // 其他情况返回原URL
    return url;
  }
  return `/api${url}`; // 生产环境返回原URL
};

// 提交状态
const submitting = ref(false)
const createFormRef = ref()

// 声明响应式变量
const selectedImageIndex = ref<number>(-1); // 用于图片排序

// 处理图片文件变化
const handleImageFilesChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files || []);
  
  if (files.length === 0) return;

  // 检查总文件数量
  if (imageList.value.length + files.length > 9) {
    ElMessage.error('图片总数不能超过9张！');
    return;
  }

  // 检查文件大小（单个文件不超过10MB，总计不超过100MB）
  const MAX_SINGLE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 100MB
  
  let totalSize = 0;
  for (const file of files) {
    if (file.size > MAX_SINGLE_FILE_SIZE) {
      ElMessage.error(`${file.name} 文件大小超过10MB，请压缩后再上传`);
      return;
    }
    totalSize += file.size;
  }
  
  // 检查总大小
  if (totalSize > MAX_TOTAL_SIZE) {
    ElMessage.error('所选图片总大小超过100MB，请减少图片或压缩后再上传');
    return;
  }

  uploadingMedia.value = true;

  try {
    // 初始化进度
    files.forEach((file, index) => {
      const fileId = Date.now() + index;
      imageUploadProgress.value[fileId] = 0;
    });

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    // 上传图片并监听进度
    const imageResponses = await uploadImages(formData, (progressEvent: any) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      // 更新所有图片的平均进度
      Object.keys(imageUploadProgress.value).forEach(key => {
        imageUploadProgress.value[Number(key)] = progress;
      });
    });

    if (imageResponses.code === 1) {
      // 添加到图片列表
      for (let i = 0; i < imageResponses.data.length; i++) {
        let url = imageResponses.data[i];
        
        // 在开发环境中，为图片URL添加/api前缀以通过代理访问
        
        const mediaInfo = await getMediaInfo(url); // 直接传递处理后的URL
        
        const newImage = {
          id: Date.now() + Math.random(),
          mediaUrl: url,
          mediaType: 1 as const, // 1 表示图片
          width: mediaInfo.width,
          height: mediaInfo.height,
          sortOrder: imageList.value.length + i + 1
        };
        
        // 使用push而不是直接赋值，以确保vuedraggable能正确追踪变化
        imageList.value.push(newImage);
      }
      
      ElMessage.success(`成功上传 ${files.length} 张图片`);
    } else {
      ElMessage.error(imageResponses.msg || '图片上传失败');
    }
  } catch (error) {
    console.error('上传图片失败:', error);
    // 检查是否是413错误
    if (error && typeof error === 'object' && 'response' in error && (error as any).response?.status === 413) {
      ElMessage.error('图片文件过大，请压缩后再上传');
    } else {
      ElMessage.error('上传图片失败，请重试');
    }
  } finally {
    // 清除进度
    imageUploadProgress.value = {};
    uploadingMedia.value = false;
    // 清空input值，以便可以重复选择同一文件
    if (target) {
      target.value = '';
    }
  }
}

// 处理视频文件变化
const handleVideoFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = Array.from(target.files || []);
  
  if (files.length === 0) return;

  // 检查视频数量（最多一个视频）
  if (videoList.value.length >= 1) {
    ElMessage.warning('最多只能上传1个视频');
    return;
  }

  // 检查文件大小（单个视频不超过500MB）
  const MAX_VIDEO_FILE_SIZE = 500 * 1024 * 1024; // 500MB
  
  const file = files[0];
  if (file.size > MAX_VIDEO_FILE_SIZE) {
    ElMessage.error(`${file.name} 文件大小超过500MB，请压缩后再上传`);
    return;
  }

  uploadingMedia.value = true;
  videoUploadProgress.value = 0; // 初始化视频上传进度

  try {
    // 上传视频并监听进度
    const videoResponse = await uploadVideo(file, (progressEvent: any) => {
      videoUploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    });

    if (videoResponse.code === 1) {
      let url = videoResponse.data;
      
      const mediaInfo = await getMediaInfo(url); // 直接传递处理后的URL
      
      const newVideo = {
        id: Date.now() + Math.random(),
        mediaUrl: url,
        mediaType: 2 as const, // 2 表示视频
        width: mediaInfo.width,
        height: mediaInfo.height,
        duration: mediaInfo.duration,
        thumbnailUrl: '' // 添加空的缩略图URL
      };
      videoList.value.push(newVideo);
      ElMessage.success('视频上传成功');
    } else {
      ElMessage.error(videoResponse.msg || '视频上传失败');
    }
  } catch (error) {
    console.error('上传视频失败:', error);
    // 检查是否是413错误
    if (error && typeof error === 'object' && 'response' in error && (error as any).response?.status === 413) {
      ElMessage.error('视频文件过大，请压缩后再上传');
    } else {
      ElMessage.error('上传视频失败，请重试');
    }
  } finally {
    videoUploadProgress.value = 0; // 清除视频上传进度
    uploadingMedia.value = false;
    // 清空input值，以便可以重复选择同一文件
    if (target) {
      target.value = '';
    }
  }
}

// 处理封面文件变化
const handleCoverFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const files = target.files;
  
  if (!files || files.length === 0) {
    console.error('未找到选择的文件');
    return;
  }

  const file = files[0];
  
  const isJPG = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/gif';
  // 降低文件大小限制，从20MB降到5MB，以避免413错误
  const isLt5M = file.size < 5 * 1024 * 1024;

  if (!isJPG) {
    ElMessage.error('上传封面图片只能是 JPG/PNG/GIF 格式!');
    // 清空input值
    target.value = '';
    return;
  }
  if (!isLt5M) {
    ElMessage.error('上传封面图片大小不能超过 5MB!');
    // 清空input值
    target.value = '';
    return;
  }

  try {
    console.log('开始上传文件:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    const response = await uploadCoverImage(file);
    
    console.log('上传响应:', response);
    
    if (response && response.code === 1) {
      let coverImageUrl = response.data;
      
      // 封面图片是OSS存储的完整URL，不需要添加/api前缀
      // 直接使用后端返回的完整URL
      
      try {
        // 对于OSS上的图片，使用原始URL获取媒体信息
        const mediaInfo = await getMediaInfo(coverImageUrl);
        form.coverImageWidth = mediaInfo.width;
        form.coverImageHeight = mediaInfo.height;
      } catch (error) {
        console.error('获取封面图片媒体信息失败:', error);
        // 即使获取媒体信息失败，也不影响主流程
      }
      
      form.coverImage = coverImageUrl;
      ElMessage.success('封面图片上传成功!');
    } else {
      const errorMsg = response?.msg || '封面图片上传失败';
      ElMessage.error(errorMsg);
    }
  } catch (error: any) {
    console.error('上传失败:', error);
    // 检查是否是413错误
    if (error && typeof error === 'object' && 'response' in error && (error as any).response?.status === 413) {
      ElMessage.error('封面图片文件过大，请压缩后再上传');
    } else {
      let errorMessage = '封面图片上传失败';
      
      if (error.response && error.response.data && error.response.data.msg) {
        errorMessage = error.response.data.msg;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      ElMessage.error(errorMessage);
    }
  } finally {
    // 清空input值，以便可以重复选择同一文件
    if (target) {
      target.value = '';
    }
  }
}

// 删除封面图片
const removeCoverImage = () => {
  form.coverImage = '';
  ElMessage.info('封面图片已删除');
}

// 触发封面上传
const triggerCoverUpload = () => {
  coverInputRef.value?.click()
}

// 触发图片上传
const triggerImageUpload = () => {
  if (imageList.value.length >= 9) {
    ElMessage.warning('最多只能上传9张图片');
    return;
  }
  imageInputRef.value?.click();
}

// 触发视频上传
const triggerVideoUpload = () => {
  if (videoList.value.length >= 1) {
    ElMessage.warning('最多只能上传1个视频');
    return;
  }
  videoInputRef.value?.click();
}

// 删除媒体
const removeMedia = (id: number, type: 'image' | 'video') => {
  if (type === 'image') {
    const index = imageList.value.findIndex(item => item.id === id);
    if (index !== -1) {
      imageList.value.splice(index, 1);
      // 重新排序
      imageList.value.forEach((item, index) => {
        item.sortOrder = index + 1;
      });
    }
  } else {
    const index = videoList.value.findIndex(item => item.id === id);
    if (index !== -1) {
      videoList.value.splice(index, 1);
    }
  }
}

// 重置表单
const resetForm = () => {
  // 重置表单数据
  form.title = ''
  form.content = ''
  form.coverImage = undefined
  imageList.value = []
  videoList.value = []
  selectedImageIndex.value = -1
  
  // 清空验证
  if (createFormRef.value) {
    createFormRef.value.clearValidate()
  }
}

// 取消发布
const cancel = () => {
  // 关闭发布帖子对话框，而不是返回上一页
  try {
    emit('cancel')
  } catch (error) {
    console.error('Error emitting cancel event:', error)
  }
}

// 处理拖拽结束事件
const onDragEnd = (event: any) => {
  console.log(`图片位置从 ${event.oldIndex} 调整至 ${event.newIndex}`);
  ElMessage.success('图片顺序已更新');
}

// 提交表单
const submitForm = async () => {
  if (!form.title.trim()) {
    ElMessage.warning('请输入帖子标题');
    return;
  }

  if (!form.content.trim()) {
    ElMessage.warning('请输入帖子内容');
    return;
  }

  try {
    submitting.value = true;
    
    // 显示审核中提示
    ElMessage.info('帖子提交成功，正在审核中...');
    
    // 合并所有媒体文件
    const allMediaItems: MediaItem[] = [
      ...imageList.value.map(item => ({ 
        mediaUrl: item.mediaUrl, 
        mediaType: item.mediaType,
        width: item.width,
        height: item.height,
        sortOrder: item.sortOrder
      })),
      ...videoList.value.map(item => ({ 
        mediaUrl: item.mediaUrl, 
        mediaType: item.mediaType,
        width: item.width,
        height: item.height,
        duration: item.duration,
        // 视频不需要排序序号，但为了接口一致性保留
        sortOrder: item.sortOrder
      }))
    ];

    const postData = {
      title: form.title,
      content: form.content,
      coverImage: form.coverImage || undefined, // 如果没有封面图片则不传递
      mediaList: allMediaItems.length > 0 ? allMediaItems : undefined // 如果没有媒体文件则不传递
    };

    console.log('正在发送发布帖子请求:', postData);
    const response = await createPostNew(postData); // 使用正确的API路径
    console.log('API响应:', response);
    
    // 检查响应数据是否存在
    if (!response) {
      ElMessage.error('发布帖子失败：响应数据为空');
      return;
    }
    
    // 检查响应数据结构
    if (typeof response !== 'object') {
      ElMessage.error('发布帖子失败：响应数据格式错误');
      return;
    }
    
    // 检查code字段是否存在
    if (response.code === undefined) {
      ElMessage.error(`发布帖子失败：缺少状态码，响应内容：${JSON.stringify(response)}`);
      return;
    }
    
    if (response.code === 1) {
      // 发布成功后，提示用户帖子正在审核中
      ElMessage.success(response.msg || '帖子发布成功！我们将在后台审核您的内容，审核结果会在个人中心显示。');
      
      // 重置表单
      resetForm();
      
      // 触发帖子创建成功的事件
      emit('post-created');
    } else {
      ElMessage.error(response.msg || '帖子发布失败');
    }
  } catch (error: any) {
    console.error('发布帖子失败:', error);
    let errorMessage = '发布帖子失败，请稍后重试';
    
    // 如果错误有详细消息，使用详细消息
    if (error.response && error.response.data && error.response.data.msg) {
      errorMessage = error.response.data.msg;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    ElMessage.error(errorMessage);
  } finally {
    submitting.value = false;
  }
}

</script>

<style scoped>
.post-create-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f0e5 0%, #e8dcc8 100%);
  padding: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.post-create-wrapper {
  width: 100%;
  max-width: 700px;
  background: #fffaf0;
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(139, 109, 75, 0.15);
  border: 1px solid #e9dcc8;
  margin-top: 20px;
  margin-bottom: 20px;
}

.el-form-item {
  margin-bottom: 15px; /* 减少表单项底部间距 */
}

.create-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px dashed #d4c19c;
}

.create-header h2 {
  font-size: 28px;
  color: #5a3e36;
  margin-bottom: 8px; /* 减少标题底部间距 */
  font-family: 'Quicksand', cursive;
}

.create-header p {
  color: #8b6d4b;
  font-size: 16px;
  margin: 0; /* 移除段落默认边距 */
}

.post-create-form {
  padding: 0;
}

.custom-input :deep(.el-input__wrapper),
.custom-textarea :deep(.el-textarea__inner) {
  border-radius: 12px;
  border: 1px solid #e9dcc8;
  background: #fefefe;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  padding: 12px 15px;
}

.custom-textarea :deep(.el-textarea__inner) {
  border-radius: 15px;
  min-height: 120px !important;
}

.custom-textarea :deep(.el-textarea__inner:focus),
.custom-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 3px rgba(212, 131, 65, 0.2) !important;
  border-color: #d48341;
}

.cover-uploader {
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 比例 = 9/16 * 100% = 56.25% */
  position: relative;
}

.cover-uploader .el-upload {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px dashed #d9c8b0;
  border-radius: 15px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9f2eb;
}

.cover-uploader .el-upload:hover {
  border-color: #d48341;
  background: #f0e6d4;
}

.cover-uploader-icon {
  font-size: 40px;
  color: #b8a188;
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

.cover-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover; /* 保持图片比例填充整个容器 */
}

.image-tip {
  margin-top: 8px; /* 减少提示文字顶部间距 */
  font-size: 13px;
  color: #a8977a;
  text-align: center;
  font-style: italic;
}

.delete-cover-btn {
  margin-top: 8px; /* 减少删除按钮顶部间距 */
  color: #e74c3c;
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  text-decoration: underline;
}

.delete-cover-btn:hover {
  color: #c0392b;
}

/* 媒体上传区域样式 */
.media-uploader-container {
  width: 100%;
}

.media-upload-area {
  width: 100%;
  height: 100px;
  border: 2px dashed #d9c8b0;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #f9f2eb;
  color: #b8a188;
  margin-bottom: 12px; /* 减少上传区域底部间距 */
}

.media-upload-area:hover {
  border-color: #d48341;
  background: #f0e6d4;
  color: #d48341;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(212, 131, 65, 0.15);
}

.media-grid-container {
  margin-top: 12px; /* 减少网格容器顶部间距 */
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  padding: 8px 0;
}

.media-item-wrapper {
  position: relative;
  aspect-ratio: 1/1;
  cursor: move; /* 添加拖拽手型光标 */
}

.media-item {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  border: 1px solid #e9dcc8;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-item:hover {
  transform: scale(1.03);
  box-shadow: 0 6px 20px rgba(212, 131, 65, 0.25);
  z-index: 2;
}

.media-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 视频特殊样式 */
video.media-preview {
  background: linear-gradient(135deg, #f0e6d4 0%, #e6dcc8 100%);
}

video.media-preview:hover {
  filter: brightness(1.05);
}

/* 视频预览样式 - 保持与图片一致 */
.video-preview {
  object-fit: cover; /* 与图片保持一致，填满容器 */
  width: 100%;
  height: 100%;
  border-radius: 12px; /* 与容器保持一致 */
  background: #f0f0f0; /* 设置背景色以增强对比度 */
}

/* 为视频添加特殊标识 */
video.media-preview::cue {
  background-color: transparent;
}

.media-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(90, 62, 54, 0.8), transparent);
  color: white;
  padding: 10px 6px 6px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.media-type {
  font-size: 10px; /* 减小媒体类型字体 */
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 500;
}

.delete-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(255, 255, 255, 0.25);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px; /* 减小删除按钮字体 */
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.delete-btn:hover {
  background: rgba(231, 76, 60, 0.8);
  transform: scale(1.1);
}

/* 选中状态 */
.media-item-wrapper.selected .media-item {
  border: 2px solid #d48341;
  box-shadow: 0 0 0 3px rgba(212, 131, 65, 0.3);
}

/* 视频单独样式 - 占一整行并按比例显示 */
.video-full-container {
  width: 100%;
  margin-top: 12px;
}

.video-item {
  position: relative;
  width: 100%;
  margin-bottom: 15px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0,0,0,0.08);
  border: 1px solid #e9dcc8;
  background: #fff;
}

.video-overlay {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
}

.video-full-preview {
  width: 100%;
  min-height: 200px;  /* 设置最小高度 */
  max-height: 400px;  /* 设置最大高度 */
  display: block;
  object-fit: contain;  /* 按比例显示，完整呈现视频 */
  background: linear-gradient(135deg, #f0e6d4 0%, #e6dcc8 100%);
}

.video-full-preview:hover {
  filter: brightness(1.05);
}

/* 为视频添加特殊标识 */
video.video-full-preview::cue {
  background-color: transparent;
}

.reorder-controls {
  margin-top: 12px; /* 减少排序控件顶部间距 */
  display: flex;
  gap: 10px; /* 减少排序按钮间隔 */
  justify-content: center;
}

.reorder-controls .el-button {
  border-radius: 20px;
  padding: 6px 14px; /* 减小排序按钮内边距 */
  font-size: 13px; /* 减小排序按钮字体 */
  background: #f9f2eb;
  border: 1px solid #d9c8b0;
  color: #8b6d4b;
}

.reorder-controls .el-button:hover {
  background: #f0e6d4;
  border-color: #d48341;
  color: #5a3e36;
  transform: translateY(-1px);
}

.form-footer {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px; /* 减少底部区域顶部间距 */
  padding-top: 18px; /* 减少底部区域内边距 */
  border-top: 1px dashed #d4c19c;
}

.submit-btn {
  background: linear-gradient(45deg, #d48341, #e6ab6a);
  border: none;
  border-radius: 25px;
  padding: 12px 30px;
  font-weight: 600;
  font-size: 16px;
  color: white;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(212, 131, 65, 0.3);
}

.submit-btn:hover,
.submit-btn:not(:disabled):hover {
  background: linear-gradient(45deg, #c06d2c, #d48341);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(212, 131, 65, 0.4);
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.reset-btn,
.cancel-btn {
  border-radius: 25px;
  padding: 12px 25px;
  font-weight: 500;
  border: 1px solid #e9dcc8;
  color: #8B7D6B;
  background: #f8f3eb;
  min-width: 100px;
}

.reset-btn:hover,
.cancel-btn:hover {
  background-color: #f0e6d4;
  color: #d48341;
  border-color: #d48341;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(212, 131, 65, 0.2);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .post-create-wrapper {
    margin: 8px; /* 减小移动端边距 */
    padding: 16px; /* 减小移动端内边距 */
    border-radius: 12px; /* 减小移动端圆角 */
  }
  
  .create-header h2 {
    font-size: 22px; /* 减小移动端标题字体 */
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 7px; /* 减小移动端网格间隙 */
  }
  
  .form-footer {
    flex-direction: column;
  }
  
  .submit-btn,
  .reset-btn,
  .cancel-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .post-create-container {
    padding: 3px; /* 减小极小屏容器边距 */
  }
  
  .post-create-wrapper {
    margin: 3px; /* 减小极小屏边距 */
    padding: 12px; /* 减小极小屏内边距 */
    border-radius: 8px; /* 减小极小屏圆角 */
  }
  
  .create-header {
    margin-bottom: 15px;
    padding-bottom: 12px;
  }
  
  .create-header h2 {
    font-size: 20px; /* 减小极小屏标题字体 */
  }
  
  .create-header p {
    font-size: 13px; /* 减小极小屏副标题字体 */
  }
  
  .post-create-form {
    padding: 0;
  }
  
  .custom-input :deep(.el-input__wrapper),
  .custom-textarea :deep(.el-textarea__inner) {
    padding: 8px; /* 减小输入框内边距 */
  }
  
  .custom-textarea :deep(.el-textarea__inner) {
    min-height: 90px !important; /* 减小极小屏文本框最小高度 */
  }
  
  .cover-uploader {
    padding-bottom: 66.25%; /* 调整比例以适应小屏幕 */
  }
  
  .image-tip {
    font-size: 11px; /* 减小极小屏提示文字 */
  }
  
  .form-footer {
    gap: 10px;
    margin-top: 15px;
    padding-top: 15px;
  }
  
  .submit-btn,
  .reset-btn,
  .cancel-btn {
    padding: 10px 18px; /* 减小极小屏按钮内边距 */
    font-size: 14px; /* 减小极小屏按钮字体 */
    min-width: auto;
  }

  .media-grid {
    grid-template-columns: repeat(3, 1fr); /* 固定3列，更适合手机 */
    gap: 5px; /* 减小极小屏网格间隙 */
  }

  .media-item {
    border-radius: 6px; /* 减小极小屏媒体项圆角 */
  }
}

/* 封面图片容器样式 */
.cover-image-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 比例 (9/16 * 100%) */
  overflow: hidden;
  margin-bottom: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.cover-image-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 保持比例并填满容器 */
  display: block;
}

.upload-progress-container {
  margin-top: 10px;
  width: 100%;
}

.upload-progress-item {
  margin-bottom: 10px;
  padding: 10px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fafafa;
}

.progress-label {
  margin-bottom: 5px;
  font-size: 14px;
  color: #606266;
}

.content-with-ai-btn {
  position: relative;
  display: inline-flex;
  width: 100%;
}

.content-with-ai-btn .custom-textarea {
  flex: 1;
}

.content-with-ai-btn .ai-btn {
  position: absolute;
  right: 8px;
  top: 8px;
  z-index: 10;
  background: linear-gradient(135deg, #5a3e36, #8b6d4b);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: bold;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(90, 62, 54, 0.3);
  transition: all 0.3s ease;
}

.content-with-ai-btn .ai-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(90, 62, 54, 0.4);
  background: linear-gradient(135deg, #8b6d4b, #a8977a);
}

.ai-polish-instruction-dialog :deep(.el-dialog) {
  background: linear-gradient(135deg, #f5f0e5, #ede6d3);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(139, 109, 75, 0.2);
  border: 1px solid #e9dcc8;
}

.ai-polish-instruction-dialog :deep(.el-dialog__header) {
  background: linear-gradient(to right, #5a3e36, #8b6d4b);
  color: white;
  border-radius: 19px 19px 0 0;
  padding: 15px 20px;
}

.ai-polish-instruction-dialog :deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

.ai-polish-instruction-dialog :deep(.el-textarea__inner) {
  border: 1px solid #e9dcc8;
  border-radius: 8px;
  padding: 12px;
  font-family: 'Open Sans', 'Nunito', 'Quicksand', sans-serif;
  background: #fdfcf9;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.05);
}

.ai-polish-instruction-dialog :deep(.el-dialog__footer) {
  background: #f8f3eb;
  border-radius: 0 0 19px 19px;
  padding: 15px 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn {
  background: #f8f3eb;
  color: #8b6d4b;
  border: 1px solid #e9dcc8;
  border-radius: 20px;
  padding: 8px 20px;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #e9dcc8;
  color: #5a3e36;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(139, 109, 75, 0.15);
}

.confirm-btn {
  background: linear-gradient(135deg, #d4a976, #a8977a);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 20px;
  transition: all 0.3s ease;
}

.confirm-btn:hover {
  background: linear-gradient(135deg, #c09565, #8b6d4b);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(212, 169, 118, 0.4);
}

.confirm-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.polish-comparison-container {
  display: flex;
  gap: 20px;
  height: 50vh;
}

.comparison-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.comparison-panel h3 {
  margin: 0 0 10px 0;
  padding-bottom: 5px;
  border-bottom: 1px solid #eee;
  color: #5a3e36;
  font-weight: 600;
  font-size: 16px;
}

.content-panel {
  flex: 1;
  border: 1px solid #e9dcc8;
  border-radius: 8px;
  padding: 15px;
  overflow-y: auto;
  background: #fffaf0;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
  font-family: 'Open Sans', 'Nunito', 'Quicksand', sans-serif;
}

.original-content pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  line-height: 1.5;
  margin: 0;
  color: #6b5b4d;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  color: #5a3e36;
  font-size: 14px;
  margin: 10px 0;
}

.loading-indicator .el-icon {
  margin-right: 10px;
}

.polish-result-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.polish-result-dialog :deep(.el-dialog) {
  background: linear-gradient(135deg, #f5f0e5, #ede6d3);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(139, 109, 75, 0.2);
  border: 1px solid #e9dcc8;
}

.polish-result-dialog :deep(.el-dialog__header) {
  background: linear-gradient(to right, #5a3e36, #8b6d4b);
  color: white;
  border-radius: 19px 19px 0 0;
  padding: 15px 20px;
}

.polish-result-dialog :deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

.comparison-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f8f3eb;
  border-radius: 0 0 19px 19px;
  padding: 15px 20px;
}

.markdown-content {
  line-height: 1.6;
  color: #5a3e36;
}

.markdown-content h1,
.markdown-content h2,
.markdown-content h3,
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  margin: 15px 0 10px 0;
  color: #5a3e36;
}

.markdown-content p {
  margin: 10px 0;
}

.markdown-content ul,
.markdown-content ol {
  padding-left: 20px;
  margin: 10px 0;
}

.markdown-content li {
  margin: 5px 0;
}

.markdown-content img {
  max-width: 100%;
  border-radius: 4px;
  margin: 10px 0;
}

.markdown-content blockquote {
  border-left: 3px solid #d48341;
  padding-left: 15px;
  margin: 15px 0;
  color: #666;
  font-style: italic;
}
</style>